import type React from "react";
import type { Preset } from "../../../../interfaces/preset-management";
import { POLICY_PRESETS } from "../../../../preferences/default-presets";
import type {
	CommenterDefinition,
	CommenterType,
} from "../../../../types/core";
import { UI_ICONS } from "../../../icons";

interface PreferenceGeneralTabProps {
	basicPolicy: string;
	maxComments: number;
	commenters: CommenterDefinition[];
	userPresets: Preset[];
	editingCommenter: CommenterDefinition | null;
	isSavingPreset: boolean;
	editingPreset: Preset | null;
	newPresetName: string;
	newPresetDescription: string;
	onApplyPreset: (id: string, name: string) => void;
	onSetEditingPreset: (preset: Preset | null) => void;
	onSetIsSavingPreset: (isSaving: boolean) => void;
	onSetNewPresetName: (name: string) => void;
	onSetNewPresetDescription: (description: string) => void;
	onDeletePreset: (id: string, name: string) => void;
	onUpdatePreset: () => void;
	onSaveCurrentAsPreset: () => void;
	onSetBasicPolicy: (policy: string) => void;
	onAddCommenter: () => void;
	onEditCommenter: (commenter: CommenterDefinition) => void;
	onDeleteCommenter: (id: string) => void;
	onSetMaxComments: (count: number) => void;
	onSetEditingCommenter: (commenter: CommenterDefinition | null) => void;
	onSaveCommenter: () => void;
}

export const PreferenceGeneralTab: React.FC<PreferenceGeneralTabProps> = ({
	basicPolicy,
	maxComments,
	commenters,
	userPresets,
	editingCommenter,
	isSavingPreset,
	editingPreset,
	newPresetName,
	newPresetDescription,
	onApplyPreset,
	onSetEditingPreset,
	onSetIsSavingPreset,
	onSetNewPresetName,
	onSetNewPresetDescription,
	onDeletePreset,
	onUpdatePreset,
	onSaveCurrentAsPreset,
	onSetBasicPolicy,
	onAddCommenter,
	onEditCommenter,
	onDeleteCommenter,
	onSetMaxComments,
	onSetEditingCommenter,
	onSaveCommenter,
}) => {
	return (
		<div className="flex flex-col gap-8">
			{editingCommenter ? (
				<div className="p-4 bg-bg-main border border-border-main rounded-lg flex flex-col gap-3 mb-4">
					<h3 className="m-0 mb-4 text-[1.1rem] text-primary border-b border-border-main pb-2">
						コメント担当の編集
					</h3>
					<div className="flex gap-3">
						<div className="flex flex-col gap-1 flex-[0_0_80px]">
							<label
								className="text-xs font-semibold text-text-dim"
								htmlFor="edit-commenter-icon"
							>
								アイコン
							</label>
							<input
								id="edit-commenter-icon"
								type="text"
								className="bg-card-bg border border-border-main rounded-md text-text-main p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
								value={editingCommenter.icon}
								placeholder="例: 💡"
								onChange={(e) =>
									onSetEditingCommenter({
										...editingCommenter,
										icon: e.target.value,
									})
								}
							/>
						</div>
						<div className="flex flex-col gap-1 flex-1">
							<label
								className="text-xs font-semibold text-text-dim"
								htmlFor="edit-commenter-name"
							>
								名前 <span className="text-red-500">*</span>
							</label>
							<input
								id="edit-commenter-name"
								type="text"
								className="bg-card-bg border border-border-main rounded-md text-text-main p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
								value={editingCommenter.name}
								placeholder="名前を入力..."
								onChange={(e) =>
									onSetEditingCommenter({
										...editingCommenter,
										name: e.target.value,
									})
								}
							/>
						</div>
						<div className="flex flex-col gap-1 flex-[0_0_100px]">
							<label
								className="text-xs font-semibold text-text-dim"
								htmlFor="edit-commenter-color"
							>
								カラー
							</label>
							<input
								id="edit-commenter-color"
								type="color"
								className="bg-card-bg border border-border-main rounded-md text-text-main p-1 h-[42px] w-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
								value={editingCommenter.color}
								onChange={(e) =>
									onSetEditingCommenter({
										...editingCommenter,
										color: e.target.value,
									})
								}
							/>
						</div>
					</div>
					<div className="flex flex-col gap-1 flex-1">
						<div className="flex justify-between items-end">
							<label
								className="text-xs font-semibold text-text-dim"
								htmlFor="edit-commenter-desc"
							>
								説明・振る舞い（プロンプト）
							</label>
							<span className="text-[10px] text-text-dim font-mono opacity-60">
								{editingCommenter.description.length} 文字
							</span>
						</div>
						<textarea
							id="edit-commenter-desc"
							className="bg-card-bg border border-border-main rounded-md text-text-main p-2.5 text-sm resize-vertical focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
							value={editingCommenter.description}
							placeholder="このキャラクターがどのようにコメントするか..."
							onChange={(e) =>
								onSetEditingCommenter({
									...editingCommenter,
									description: e.target.value,
								})
							}
							rows={4}
						/>
					</div>
					<div className="flex justify-end gap-2 mt-2">
						<button
							type="button"
							className="bg-transparent border border-border-main text-text-dim px-4 py-2 rounded-md cursor-pointer transition-all hover:border-text-dim hover:text-text-main focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none active:scale-95"
							onClick={() => onSetEditingCommenter(null)}
						>
							キャンセル
						</button>
						<button
							type="button"
							className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white border-none rounded-md cursor-pointer font-semibold transition-all hover:bg-primary-hover focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-main focus-visible:outline-none disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
							onClick={onSaveCommenter}
							disabled={!editingCommenter.name.trim()}
							title={
								!editingCommenter.name.trim() ? "名前を入力してください" : ""
							}
						>
							{UI_ICONS.SAVE} 保存
						</button>
					</div>
				</div>
			) : (
				<>
					<div className="flex flex-col gap-4">
						<h3 className="m-0 mb-4 text-[1.1rem] text-primary border-b border-border-main pb-2">
							おすすめプリセット
						</h3>
						<div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3 mt-3 max-[480px]:grid-cols-1">
							{POLICY_PRESETS.map((preset) => (
								<button
									type="button"
									key={preset.id}
									className="bg-bg-main border border-border-main rounded-[10px] p-4 cursor-pointer transition-all duration-200 flex flex-col items-center text-center gap-1 relative hover:border-primary hover:bg-primary/5 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none group active:scale-95 animate-in fade-in zoom-in duration-300"
									onClick={() => onApplyPreset(preset.id, preset.name)}
								>
									<div className="text-2xl" role="img" aria-hidden="true">
										{preset.icon}
									</div>
									<div className="font-semibold text-sm">{preset.name}</div>
									<div className="text-[11px] text-text-dim">
										{preset.description}
									</div>
								</button>
							))}
						</div>

						{userPresets.length > 0 && (
							<div className="mt-8">
								<h3 className="m-0 mb-4 text-[1.1rem] text-primary border-b border-border-main pb-2">
									保存したプリセット
								</h3>
								<div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3 mt-3 max-[480px]:grid-cols-1">
									{userPresets.map((preset) => (
										<button
											type="button"
											key={preset.id}
											className="bg-bg-main border border-border-main rounded-[10px] p-4 cursor-pointer transition-all duration-200 flex flex-col items-center text-center gap-1 relative hover:border-primary hover:bg-primary/5 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none group active:scale-95 animate-in fade-in zoom-in duration-300"
											onClick={() => onApplyPreset(preset.id, preset.name)}
										>
											<div className="text-2xl" role="img" aria-hidden="true">
												{preset.icon || "📋"}
											</div>
											<div className="font-semibold text-sm">{preset.name}</div>
											<div className="text-[11px] text-text-dim">
												{preset.description || "ユーザー作成"}
											</div>
											<div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity z-[2]">
												<button
													type="button"
													className="bg-white/10 border border-border-main text-text-dim cursor-pointer p-1 rounded transition-all text-[0.9rem] flex items-center justify-center hover:bg-white/20 hover:text-text-main hover:border-text-dim focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none active:scale-95"
													title={`プリセット「${preset.name}」を編集`}
													aria-label={`プリセット「${preset.name}」を編集`}
													onClick={(e) => {
														e.stopPropagation();
														onSetEditingPreset(preset);
														onSetNewPresetName(preset.name);
														onSetNewPresetDescription(preset.description || "");
														onSetIsSavingPreset(false);
													}}
												>
													{UI_ICONS.EDIT}
												</button>
												<button
													type="button"
													className="bg-white/10 border border-border-main text-text-dim cursor-pointer p-1 rounded transition-all text-[0.9rem] flex items-center justify-center hover:bg-red-500/10 hover:text-red-500 hover:border-red-500 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:outline-none active:scale-95"
													title={`プリセット「${preset.name}」を削除`}
													aria-label={`プリセット「${preset.name}」を削除`}
													onClick={(e) => {
														e.stopPropagation();
														onDeletePreset(preset.id, preset.name);
													}}
												>
													{UI_ICONS.DELETE}
												</button>
											</div>
										</button>
									))}
								</div>
							</div>
						)}
					</div>

					<div className="mt-12 p-6 bg-primary/5 rounded-xl border border-dashed border-primary">
						<div className="m-0 mb-2 text-lg font-semibold">
							{editingPreset
								? "プリセットの編集"
								: "現在の構成をプリセットに保存"}
						</div>
						{isSavingPreset || editingPreset ? (
							<div className="flex flex-col gap-3">
								<div className="flex flex-col gap-1">
									<div className="flex justify-between items-end">
										<label
											htmlFor="preset-name"
											className="text-xs font-semibold text-text-dim"
										>
											プリセット名 <span className="text-red-500">*</span>
										</label>
										<span className="text-[10px] text-text-dim font-mono opacity-60">
											{newPresetName.length} 文字
										</span>
									</div>
									<input
										id="preset-name"
										className="bg-card-bg border border-border-main rounded-md text-text-main p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
										type="text"
										value={newPresetName}
										onChange={(e) => onSetNewPresetName(e.target.value)}
										placeholder="例: 会議用、アイデア出し用..."
									/>
								</div>
								<div className="flex flex-col gap-1">
									<div className="flex justify-between items-end">
										<label
											htmlFor="preset-desc"
											className="text-xs font-semibold text-text-dim"
										>
											説明
										</label>
										<span className="text-[10px] text-text-dim font-mono opacity-60">
											{newPresetDescription.length} 文字
										</span>
									</div>
									<input
										id="preset-desc"
										className="bg-card-bg border border-border-main rounded-md text-text-main p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
										type="text"
										value={newPresetDescription}
										onChange={(e) => onSetNewPresetDescription(e.target.value)}
										placeholder="このプリセットの用途..."
									/>
								</div>
								<div className="flex justify-end gap-2 mt-4">
									<button
										type="button"
										className="bg-transparent border border-border-main text-text-dim px-4 py-2 rounded-md cursor-pointer transition-all hover:border-text-dim hover:text-text-main focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none active:scale-95"
										onClick={() => {
											onSetIsSavingPreset(false);
											onSetEditingPreset(null);
											onSetNewPresetName("");
											onSetNewPresetDescription("");
										}}
									>
										キャンセル
									</button>
									<button
										type="button"
										className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white border-none rounded-md cursor-pointer font-semibold transition-all hover:bg-primary-hover focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-main focus-visible:outline-none disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
										onClick={
											editingPreset ? onUpdatePreset : onSaveCurrentAsPreset
										}
										disabled={!newPresetName.trim()}
										title={
											!newPresetName.trim()
												? "プリセット名を入力してください"
												: ""
										}
									>
										{UI_ICONS.SAVE}{" "}
										{editingPreset
											? "プリセットを更新する"
											: "この構成を保存する"}
									</button>
								</div>
							</div>
						) : (
							<button
								type="button"
								className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white border-none rounded-md cursor-pointer font-semibold transition-all hover:bg-primary-hover focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-main focus-visible:outline-none w-full justify-center active:scale-95"
								onClick={() => onSetIsSavingPreset(true)}
							>
								{UI_ICONS.PRESET} 現在の基本方針とコメント担当を保存
							</button>
						)}
					</div>

					<div className="mt-12 flex flex-col gap-4">
						<div className="flex justify-between items-end">
							<label
								htmlFor="basic-policy-textarea"
								className="text-lg font-semibold text-text-main cursor-pointer"
							>
								AIの基本キャラクター/プロンプト
							</label>
							<span className="text-[10px] text-text-dim font-mono opacity-60">
								{basicPolicy.length} 文字
							</span>
						</div>
						<textarea
							id="basic-policy-textarea"
							className="w-full min-h-[120px] p-3 bg-bg-main border border-border-main rounded-lg text-text-main font-inherit text-sm resize-vertical focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
							value={basicPolicy}
							onChange={(e) => onSetBasicPolicy(e.target.value)}
							placeholder="AIの振る舞いを記述..."
						/>
					</div>

					<div className="mt-12 flex flex-col gap-4">
						<div className="flex justify-between items-center mb-4">
							<h3 className="m-0 text-lg font-semibold">
								コメント担当（ロール）
							</h3>
							<button
								type="button"
								className="bg-transparent border border-border-main text-text-dim px-4 py-2 rounded-md cursor-pointer transition-all hover:border-text-dim hover:text-text-main focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none active:scale-95"
								onClick={onAddCommenter}
							>
								{UI_ICONS.ADD} 追加
							</button>
						</div>
						<div className="flex flex-col gap-2">
							{commenters.map((commenter) => (
								<div
									key={commenter.id as CommenterType}
									className="bg-bg-main border border-border-main rounded-lg overflow-hidden animate-in fade-in slide-in-from-left-5 duration-300"
								>
									<div className="flex items-center p-3 gap-3">
										<span
											className="text-xl shrink-0"
											style={{ color: commenter.color }}
											role="img"
											aria-hidden="true"
										>
											{commenter.icon}
										</span>
										<span className="font-semibold w-20 shrink-0">
											{commenter.name}
										</span>
										<span className="flex-1 text-[13px] text-text-dim">
											{commenter.description}
										</span>
										<div className="flex gap-1 shrink-0">
											<button
												type="button"
												className="bg-transparent border-none cursor-pointer p-1 rounded transition-all text-xl flex items-center justify-center hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none active:scale-95"
												title={`コメント担当「${commenter.name}」を編集`}
												aria-label={`コメント担当「${commenter.name}」を編集`}
												onClick={() => onEditCommenter(commenter)}
											>
												{UI_ICONS.EDIT}
											</button>
											<button
												type="button"
												className="bg-transparent border-none cursor-pointer p-1 rounded transition-all text-xl flex items-center justify-center hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:outline-none active:scale-95"
												title={`コメント担当「${commenter.name}」を削除`}
												aria-label={`コメント担当「${commenter.name}」を削除`}
												onClick={() => onDeleteCommenter(commenter.id)}
											>
												{UI_ICONS.DELETE}
											</button>
										</div>
									</div>
								</div>
							))}
						</div>
					</div>

					<div className="mt-12 flex flex-col gap-4">
						<h3 className="m-0 text-lg font-semibold">生成設定</h3>
						<div className="flex flex-col gap-2 bg-bg-main p-3 rounded-lg border border-border-main mb-3">
							<label
								className="text-xs font-semibold text-text-dim"
								htmlFor="max-comments-range"
							>
								最大生成コメント数: {maxComments}個
							</label>
							<input
								id="max-comments-range"
								className="cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-main rounded-lg"
								type="range"
								min="1"
								max={Math.max(1, commenters.length)}
								step="1"
								value={maxComments}
								onChange={(e) => onSetMaxComments(parseInt(e.target.value, 10))}
							/>
						</div>
					</div>
				</>
			)}
		</div>
	);
};
